import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import supabase from '../lib/supabase';
import ShareButton from '../components/ShareButton';
import MatchPhotoCarousel from '../components/MatchPhotoCarousel';

const formatDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleDateString('el-GR', { year: 'numeric', month: 'long', day: 'numeric' });
};

const isPublicArticle = (article) => {
  if (!article) return false;
  const status = article.status || 'published';
  if (status === 'draft') return false;
  if (!article.published_at) return status === 'published';
  const publishTime = new Date(article.published_at).getTime();
  return Number.isNaN(publishTime) ? status === 'published' : publishTime <= Date.now();
};

export default function NewsDetailPage() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      const { data, error: queryError } = await supabase.from('news_aox1234').select('*').eq('id', id).maybeSingle();
      if (!active) return;
      if (queryError) {
        setError(queryError.message || 'Δεν ήταν δυνατή η φόρτωση της ανάρτησης.');
        setArticle(null);
      } else if (!isPublicArticle(data)) {
        setError('Η ανάρτηση δεν είναι διαθέσιμη.');
        setArticle(null);
      } else {
        setArticle(data);
      }
      setLoading(false);
    };
    load();
    return () => { active = false; };
  }, [id]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center bg-gray-50"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600" /></div>;
  }

  if (error || !article) {
    return (
      <div className="min-h-[60vh] bg-gray-50 py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{error || 'Η ανάρτηση δεν βρέθηκε.'}</h1>
          <Link to="/news" className="text-primary-700 font-semibold hover:underline">← Επιστροφή στα Νέα</Link>
        </div>
      </div>
    );
  }

  const image = article.image || article.image_url;
  const description = article.excerpt || article.content || '';
  const photoReportMatchId = article.matchday_post_key?.startsWith('photo-report:') ? article.matchday_post_key.slice('photo-report:'.length) : null;

  return (
    <article className="min-h-screen bg-gray-50 py-10 md:py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <Link to="/news" className="inline-flex mb-6 text-primary-700 font-semibold hover:underline">← Νέα & Ανακοινώσεις</Link>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {!photoReportMatchId && image && (
            <div className="bg-slate-100 border-b border-slate-200 flex items-center justify-center">
              <img src={image} alt={article.title} className="block max-h-[78vh] w-full object-contain" />
            </div>
          )}

          <div className="p-6 md:p-10">
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mb-4">
              {article.category && <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-800 font-semibold">{article.category}</span>}
              <span>{formatDate(article.published_at || article.created_at)}</span>
            </div>

            <div className="flex gap-4 items-start justify-between">
              <h1 className="text-3xl md:text-4xl font-black leading-tight text-gray-900">{article.title}</h1>
              <ShareButton title={article.title} description={description} imageUrl={image} compact={true} variant="outline" />
            </div>

            {article.excerpt && <p className="mt-5 text-lg font-medium text-gray-600">{article.excerpt}</p>}
            {photoReportMatchId && <div className="mt-7"><MatchPhotoCarousel matchId={photoReportMatchId} title="Φωτορεπορτάζ" /></div>}
            {article.content && <div className="mt-7 whitespace-pre-wrap text-gray-800 leading-relaxed text-lg">{article.content}</div>}

            {article.source_url && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <a href={article.source_url} className="inline-flex items-center text-primary-700 font-bold hover:underline">Άνοιγμα Match Center →</a>
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
